import { useState } from 'react';
import { Card, Tag, Typography, Space, Empty, Collapse, Badge } from 'antd';
import {
    CalendarOutlined,
    ClockCircleOutlined,
    MedicineBoxOutlined,
    RightOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { InputFromUser } from '../../../components/Input/input';

const { Title, Text } = Typography;
const { Panel } = Collapse;

/**
 * Redesigned TreatmentCard with a premium, collapsible interface.
 * Features a modern gradient header and grouped service views.
 */
const TreatmentCard = ({ plan, formatTime, handlePlanChange }) => {
    const [activeKeys, setActiveKeys] = useState(['0']); // Default expand first date

    if (!plan) return null;

    const renderEmpty = () => (
        <div style={{ padding: '40px', textAlign: 'center' }}>
            <Empty description="No service details available" />
        </div>
    );

    const serviceDetailsArray = Object.entries(plan.serviceDetails || {});

    return (
        <div style={{ marginBottom: '24px' }}>
            {/* Treatment Main Card */}
            <Card
                className="premium-treatment-card"
                bordered={false}
                style={{
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
                    background: '#fff'
                }}
                bodyStyle={{ padding: 0 }}
            >
                {/* Modern Gradient Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    padding: '24px 30px',
                    color: '#fff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Space size={16}>
                        <div style={{
                            width: '50px',
                            height: '50px',
                            background: 'rgba(255,255,255,0.2)',
                            borderRadius: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(5px)'
                        }}>
                            <MedicineBoxOutlined style={{ fontSize: '24px' }} />
                        </div>
                        <div>
                            <Title level={3} style={{ margin: 0, color: '#fff', fontWeight: 700, letterSpacing: '-0.02em' }}>
                                {plan.treatmentName}
                            </Title>
                            <Space split={<span style={{ opacity: 0.5 }}>•</span>} style={{ fontSize: '13px', opacity: 0.9 }}>
                                <span>{serviceDetailsArray.length} Session Days</span>
                                <span>Plan ID: {plan._id.slice(-6).toUpperCase()}</span>
                            </Space>
                        </div>
                    </Space>

                    <Badge
                        count="Active Plan"
                        style={{ backgroundColor: '#52c41a', borderRadius: '4px' }}
                    />
                </div>

                {/* Collapsible Date Sections */}
                <div style={{ padding: '20px' }}>
                    {serviceDetailsArray.length === 0 ? renderEmpty() : (
                        <Collapse
                            activeKey={activeKeys}
                            onChange={setActiveKeys}
                            ghost
                            expandIcon={({ isActive }) => (
                                <RightOutlined rotate={isActive ? 90 : 0} style={{ color: '#1890ff', fontSize: '14px' }} />
                            )}
                            style={{ background: 'transparent' }}
                        >
                            {serviceDetailsArray.map(([date, services], index) => (
                                <Panel
                                    header={
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: '20px' }}>
                                            <Space size={12}>
                                                <div style={{
                                                    background: '#f0f7ff',
                                                    color: '#1890ff',
                                                    padding: '8px 12px',
                                                    borderRadius: '10px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px'
                                                }}>
                                                    <CalendarOutlined />
                                                    <Text strong style={{ color: '#1890ff' }}>
                                                        {moment(date).format('DD MMM YYYY')}
                                                    </Text>
                                                </div>
                                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                                    {moment(date).format('dddd')}
                                                </Text>
                                            </Space>
                                            <Badge
                                                count={`${services.length} Services`}
                                                style={{ backgroundColor: '#f0f0f0', color: '#8c8c8c', boxShadow: 'none' }}
                                            />
                                        </div>
                                    }
                                    key={index.toString()}
                                    style={{
                                        marginBottom: '16px',
                                        background: '#fff',
                                        borderRadius: '14px',
                                        border: '1px solid #f0f0f0',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <div style={{ padding: '0 10px 10px' }}>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                                            gap: '12px'
                                        }}>
                                            {services.map((service, sIndex) => {
                                                const hasValue = service.medicineValue &&
                                                    service.medicineValue !== 'Not Started' &&
                                                    service.medicineValue !== 'On Hold' &&
                                                    service.medicineValue !== 'Not Taken';

                                                return (
                                                    <div
                                                        key={service._id || sIndex}
                                                        style={{
                                                            background: hasValue ? '#f6ffed' : '#f8fafc',
                                                            borderRadius: '12px',
                                                            padding: '16px',
                                                            border: hasValue ? '1px solid #b7eb8f' : '1px solid #edf2f7',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: '12px',
                                                            position: 'relative',
                                                            transition: 'all 0.2s ease',
                                                            boxShadow: hasValue ? '0 2px 8px rgba(82, 196, 26, 0.08)' : 'none'
                                                        }}
                                                        className="service-item-card"
                                                    >
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                            <div style={{ flex: 1 }}>
                                                                <Text strong style={{ fontSize: '15px', color: hasValue ? '#1b5e20' : '#2d3748', display: 'block' }}>
                                                                    {service.serviceName}
                                                                </Text>
                                                                <Space style={{ color: '#718096', fontSize: '12px', marginTop: '4px' }}>
                                                                    <ClockCircleOutlined />
                                                                    <span>{formatTime(service.startTime)} - {formatTime(service.endTime)}</span>
                                                                </Space>
                                                            </div>
                                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                                                                <Tag color={hasValue ? "success" : "blue"} bordered={false} style={{ margin: 0, borderRadius: '6px' }}>
                                                                    {service.totalMinutes} min
                                                                </Tag>
                                                                {hasValue && <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />}
                                                            </div>
                                                        </div>

                                                        <div style={{ marginTop: '4px' }}>
                                                            <InputFromUser
                                                                placeholder="Select Status"
                                                                type="singleSelect"
                                                                name={`status-${service._id}`}
                                                                onChange={newValue => handlePlanChange(plan._id, service._id, date, newValue)}
                                                                value={service.medicineValue || undefined}
                                                                options={[
                                                                    ...(service?.medicine.filter((item) => (typeof item === 'string' ? item : item?.value)?.trim().toUpperCase() !== 'HOLD') || []),
                                                                    { label: '⏸️ On Hold', value: 'On Hold' },
                                                                    { label: '❌ Not Taken', value: 'Not Taken' },
                                                                    { label: '⏹️ Not Started', value: 'Not Started' }
                                                                ]}
                                                                Showlabel={false}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </Panel>
                            ))}
                        </Collapse>
                    )}
                </div>
            </Card>

            <style dangerouslySetInnerHTML={{
                __html: `
                .premium-treatment-card .ant-collapse-header {
                    padding: 16px 20px !important;
                    align-items: center !important;
                }
                .service-item-card:hover {
                    border-color: #1890ff !important;
                    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.1);
                    transform: translateY(-2px);
                }
            `}} />
        </div>
    );
};

export default TreatmentCard;
